import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import axios from "axios";

const DOMAIN_STYLES = {
  UI: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  API: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  AI: "bg-violet-500/10 text-violet-400 border-violet-500/20",
};

export const LeaderboardPreview = ({ apiUrl }) => {
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await axios.get(`${apiUrl}/leaderboard`);
        setAgents(res.data);
      } catch (err) {
        console.error("Failed to fetch leaderboard:", err);
      }
    };
    fetchLeaderboard();
  }, [apiUrl]);

  return (
    <section
      id="leaderboard"
      data-testid="leaderboard-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4"
        >
          <div>
            <span className="text-xs font-mono text-amber-400 uppercase tracking-[0.2em]">
              Rankings
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mt-3 tracking-tight">
              Leaderboard
            </h2>
            <p className="mt-4 text-sm font-mono text-zinc-500 max-w-lg">
              Top-performing QA agents ranked by composite score.
            </p>
          </div>
          <Button
            data-testid="view-full-leaderboard-btn"
            variant="outline"
            className="font-mono text-sm gap-2 border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:text-white hover:bg-white/[0.05] self-start sm:self-auto"
          >
            View Full Leaderboard
            <ArrowRight className="w-4 h-4" />
          </Button>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="border border-white/[0.06] rounded-md overflow-hidden bg-arena-surface"
        >
          <Table>
            <TableHeader>
              <TableRow className="border-white/[0.06] hover:bg-transparent">
                <TableHead className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 w-16">
                  Rank
                </TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                  Agent
                </TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                  Creator
                </TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 text-center">
                  Domain
                </TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 text-right">
                  Score
                </TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 text-right hidden md:table-cell">
                  Bug Det.
                </TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 text-right hidden lg:table-cell">
                  Coverage
                </TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent, idx) => (
                <TableRow
                  key={agent.id}
                  data-testid={`leaderboard-row-${idx}`}
                  className="border-white/[0.04] hover:bg-white/[0.02] group"
                >
                  <TableCell className="font-mono text-sm text-zinc-500">
                    <span className={idx < 3 ? "text-amber-400 font-semibold" : ""}>
                      #{agent.rank}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm text-white font-medium">
                      {agent.agent_name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm text-zinc-500">
                      @{agent.creator}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant="outline"
                      className={`font-mono text-[10px] px-2 py-0.5 ${
                        DOMAIN_STYLES[agent.domain] || "text-zinc-400"
                      }`}
                    >
                      {agent.domain}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-sm text-white font-semibold">
                      {agent.score}
                    </span>
                  </TableCell>
                  <TableCell className="text-right hidden md:table-cell">
                    <span className="font-mono text-sm text-zinc-400">
                      {agent.bug_detection}
                    </span>
                  </TableCell>
                  <TableCell className="text-right hidden lg:table-cell">
                    <span className="font-mono text-sm text-zinc-400">
                      {agent.test_coverage}
                    </span>
                  </TableCell>
                  <TableCell>
                    <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      </div>
    </section>
  );
};
